/**
 * @fileoverview Home Dashboard Page
 * @description Main dashboard with app cards for navigation to different modules
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '@/config';
import { 
  Github, 
  CalendarDays,
  Ticket,
  FlaskConical,
  ExternalLink,
  Bug,
  PackageCheck,
  Database,
  Construction,
  Cpu,
  Bot,
  Activity,
  GitBranch,
  BarChart3,
  Sparkles,
  HeartPulse,
  ScanSearch,
  Radar,
} from 'lucide-react';
import './Home.css';

const appCards = [
  {
    id: 'ai-evaluation-results',
    title: 'AI Evals',
    description: 'Review consolidated evaluator scores, inspect details, and generate AI insights for every run.',
    icon: ScanSearch,
    path: '/ai-evaluation-results',
    color: '#007F85',
  },
  {
    id: 'scout-eval',
    title: 'Scout Evals',
    description: 'Import Scout consolidated reports, track evaluator scores, and compare every run over time.',
    icon: Radar,
    path: '/scout-eval',
    color: '#0097A9',
  },
  {
    id: 'github-metrics',
    title: 'Copilot Metrics',
    description: 'Track GitHub Copilot usage, acceptance rates, and productivity metrics across your organization.',
    icon: Github,
    path: '/github-metrics',
    color: '#0097A9',
  },
  {
    id: 'pnr-generator',
    title: 'PNR Generator',
    description: 'Generate Passenger Name Records for QA testing environments with configurable booking parameters.',
    icon: Ticket,
    path: '/pnr-generator',
    color: '#7C3AED',
  },
  {
    id: 'agentic-test-data-orchestrator',
    title: 'Agentic Test Data Orchestrator',
    description: 'Generate flight, PNR, delay, and other test data scenarios through natural-language agent orchestration.',
    icon: Sparkles,
    path: '/ai-test-data-generation',
    color: '#6366F1',
  },
  {
    id: 'test-calendar',
    title: 'Test Calendar',
    description: 'View and manage test schedules, milestones, and release calendar for your QE teams.',
    icon: CalendarDays,
    path: '/test-calendar',
    color: '#01426A',
  },
  {
    id: 'model-context-protocol',
    title: 'AI Native Test Automation',
    description: 'End-to-end test automation workflow — from user story analysis to automated test execution using AI and MCP.',
    icon: Cpu,
    path: '/model-context-protocol',
    color: '#6366F1',
  },
  {
    id: 'agentic-e2e-test-orchestrator',
    title: 'Agentic E2E Test Orchestrator',
    description: 'AI-driven end-to-end test orchestration with autonomous agent-based test planning and execution.',
    icon: Bot,
    path: 'about:blank',
    color: '#0EA5E9',
    external: true,
  },
  {
    id: 'test-automation-leverage',
    title: 'AI Leveraged Test Generation',
    description: 'Track auto-generated test cases across Azure DevOps projects and measure test automation adoption.',
    icon: Activity,
    path: '/ai-leverage',
    color: '#0EA5E9',
  },
  {
    id: 'pipeline-service',
    title: 'Pipeline Service',
    description: 'Select an Azure DevOps project to start pipeline-level analysis and diagnostics.',
    icon: GitBranch,
    path: '/pipeline-service',
    color: '#0F766E',
  },
  {
    id: 'test-automation-leverage-report',
    title: 'Test Automation Leverage Report',
    description: 'Generate project-wise automation savings reports using Azure DevOps pipeline runs, test cases, and weighted effort formulas.',
    icon: BarChart3,
    path: '/project-savings-report',
    color: '#2563EB',
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'Generate AI-powered validation reports for incidents — semantic & agentic severity/priority analysis.',
    icon: BarChart3,
    path: '/reports',
    color: '#7C3AED',
  },
  {
    id: 'azure-function-health',
    title: 'Azure Function Health',
    description: 'Monitor Azure Function endpoint compliance, availability, and scheduled health probes by environment.',
    icon: HeartPulse,
    path: '/azure-function-health',
    color: '#008653',
  },
  {
    id: 'regression-pack-optimizer',
    title: 'Regression Pack Optimizer',
    description: 'Intelligent test suite optimization to reduce regression cycle time while maintaining coverage.',
    icon: PackageCheck,
    path: '/regression-optimizer',
    color: '#D97706',
  },
];

const testGenerationCard = {
  id: 'test-case-generator',
  title: 'AI Based Test Case Generator (RAG)',
  description: 'AI-powered test case generation from requirements using generative AI models.',
  icon: FlaskConical,
  path: 'https://genai-test-gen-app.azurewebsites.net/',
  color: '#059669',
  external: true,
};

const visibleAppCards = APP_CONFIG.features.enableTestGenerationLink
  ? [appCards[0], testGenerationCard, ...appCards.slice(1)]
  : appCards;

const wipCards = [
  {
    id: 'production-defect-rca',
    title: 'Production Defect RCA',
    description: 'Automated root cause analysis for production defects using AI-driven log correlation.',
    icon: Bug,
    color: '#DC2626',
  },
  {
    id: 'agentic-test-data-mgmt',
    title: 'Agentic Test Data Mgmt',
    description: 'AI-agent powered test data management for dynamic provisioning and synthetic data generation.',
    icon: Database,
    color: '#2563EB',
  },
];

const AppCard = ({ title, description, icon: Icon, path, color, external }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (external) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  };

  return (
    <div 
      className="app-card" 
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
      style={{ '--card-color': color }}
    >
      <div className="app-card-header">
        <div className="app-card-icon" style={{ background: `${color}15`, color }}>
          <Icon size={28} />
        </div>
        <h3 className="app-card-title">
          {title}
          {external && <ExternalLink size={14} style={{ marginLeft: 6, opacity: 0.5, verticalAlign: 'middle' }} />}
        </h3>
      </div>
      <p className="app-card-description">{description}</p>
      <div className="app-card-footer">
        <span className="app-card-link">Open →</span>
      </div>
    </div>
  );
};

const WipCard = ({ title, description, icon: Icon, color }) => {
  return (
    <div 
      className="app-card wip-card" 
      style={{ '--card-color': color }}
      title="Work in Progress"
    >
      <div className="app-card-header">
        <div className="app-card-icon" style={{ background: `${color}15`, color }}>
          <Icon size={28} />
        </div>
        <h3 className="app-card-title">{title}</h3>
        <span className="wip-badge"><Construction size={12} /> WIP</span>
      </div>
      <p className="app-card-description">{description}</p>
      <div className="app-card-footer">
        <span className="app-card-link">Coming Soon</span>
      </div>
    </div>
  );
};

export const Home = () => {
  return (
    <div className="home-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Quality Engineering</h1>
          <p className="page-description">
            Welcome to Alaska Airlines Quality Engineering. Access all your tools, metrics, and reports in one place.
          </p>
        </div>
      </div>

      {/* App Cards */}
      <section className="apps-section">
        <h2 className="section-heading">Applications</h2>
        <div className="apps-grid">
          {visibleAppCards.map((app) => (
            <AppCard key={app.id} {...app} />
          ))}
          {wipCards.map((app) => (
            <WipCard key={app.id} {...app} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
